import { useCallback, useEffect, useRef, useState } from 'react';

import { runIsDevGitHubAccountEnabled, runRequestJson } from './appRunData';
import type { RunIssueViewModelEnhanced } from '../../run/RunIssueDashboard';
import type { AppPentestTarget } from './appScanApi';
import { getWorkspaceRootPath } from './appWorkspaceRoot';
import { isValidAccountEmail } from './useAppAccount';

type AppChatRole = 'user' | 'assistant';
export type AppScanIntent = 'pentest' | 'pricing';

export interface AppWorkspaceResourcePointer {
  id: string;
  workspaceId: string;
  kind: 'codebase' | 'issues' | 'docs';
  name: string;
  state: 'ready' | 'processing' | 'failed' | 'archived';
  source?: 'github' | 'upload';
}

/** A finding attached to a message; opens the issue panel and supplies context to the chat agent. */
export interface AppIssueRef {
  key: string;
  title: string;
  severity: RunIssueViewModelEnhanced['severity'];
  summary?: string;
  detail?: string;
  impact?: string;
  files?: string[];
}

/** Interactive content an assistant message can carry. */
type AppChatWidget =
  | { type: 'connect-github'; intent: AppScanIntent }
  | { type: 'repo-picker'; intent: AppScanIntent; chosen?: string }
  /** The run card; every pentest runs the active custom workflow or built-in default, with no tier choice. */
  | { type: 'pricing'; intent: AppScanIntent; target: AppPentestTarget; launched?: boolean }
  | { type: 'pentest'; runId: string }
  | { type: 'plans' }
  /** Asks for the account email before a ZIP pentest; the user answers in the composer. */
  | { type: 'email-request'; target: AppPentestTarget; pricingMessageId: string; email?: string };

export type AppEmailRequestWidget = Extract<AppChatWidget, { type: 'email-request' }>;

/** A failure the backend already answered with a saved assistant message, so it needs no second reply. */
export class AppChatReportedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AppChatReportedError';
  }
}

export interface AppChatMessage {
  id: string;
  role: AppChatRole;
  content: string;
  resourceIds: string[];
  widget?: AppChatWidget;
  issueRefs?: AppIssueRef[];
  /** The backend routed this reply to the Codex coding agent instead of the chat model. */
  isCodex?: boolean;
  /** The backend picked or generated the pentest workflow from the request. */
  isAutoWorkflow?: boolean;
  createdAt: string;
}

export interface AppChat {
  id: string;
  workspaceId: string;
  title: string;
  messages: AppChatMessage[];
  createdAt: string;
  updatedAt: string;
}

interface ApiChatMessage {
  id: string;
  role: string;
  content: string;
  resourceIds?: unknown;
  metadata?: unknown;
  createdAt: string;
}

interface ApiChat {
  id: string;
  workspaceId: string;
  title: string;
  messages?: ApiChatMessage[];
  createdAt: string;
  updatedAt: string;
}

const CHAT_STORAGE_KEY = 'havu-app-chats-v1';
const CHAT_TITLE_MAX_LENGTH = 48;
const ASSISTANT_REPLY_DELAY_MS = 700;
const REPLY_ROUTE_POLL_MS = 1500;
// Long messages are pasted material (reports, logs), not commands.
const INTENT_MAX_LENGTH = 200;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const createId = (): string => {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

/** Recognises requests the app can act on itself, without a chat model. */
export const detectScanIntent = (content: string): AppScanIntent | null => {
  const text = content.toLowerCase().trim();

  // Analysis requests and questionnaire answers mention scans and audits without asking for one.
  if (text.length > INTENT_MAX_LENGTH || /^(analy[sz]e|help me answer)\b/.test(text)) {
    return null;
  }

  if (/\b(price|prices|pricing|cost|costs|quote|how much)\b/.test(text)) {
    return 'pricing';
  }

  // Pentests, security scans, assessments and (smart contract) audits all start the same workflow.
  if (/\b(pen-?tests?|pen-?testing|pen tests?|penetration tests?|penetration testing|scans?|scanning|audits?|auditing|assess|assessments?|security reviews?)\b/.test(text)) {
    return 'pentest';
  }

  return null;
};

/** Picks the email address out of a chat reply such as "sure, it's name@example.com". */
export const findEmailAddress = (content: string): string | undefined => {
  const match = /[^\s@,;<>()[\]'"`]+@[^\s@,;<>()[\]'"`]+\.[^\s@,;<>()[\]'"`]+/.exec(content);
  const email = match?.[0].replace(/[.:!?]+$/, '');

  return email && isValidAccountEmail(email) ? email : undefined;
};

/** The email request the chat is waiting on, when it is the latest message and still unanswered. */
export const getPendingEmailRequest = (
  chat: AppChat | undefined
): { messageId: string; widget: AppEmailRequestWidget } | undefined => {
  const last = chat?.messages[chat.messages.length - 1];

  return last?.role === 'assistant' && last.widget?.type === 'email-request' && !last.widget.email
    ? { messageId: last.id, widget: last.widget }
    : undefined;
};

const createMessage = (role: AppChatRole, content: string, widget?: AppChatWidget): AppChatMessage => ({
  id: createId(),
  role,
  content,
  resourceIds: [],
  ...(widget ? { widget } : {}),
  createdAt: new Date().toISOString()
});

const deriveChatTitle = (content: string): string => {
  const singleLine = content.replace(/\s+/g, ' ').trim();

  return singleLine.length > CHAT_TITLE_MAX_LENGTH
    ? `${singleLine.slice(0, CHAT_TITLE_MAX_LENGTH - 1).trimEnd()}…`
    : singleLine || 'New chat';
};

const readStoredChats = (): AppChat[] => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CHAT_STORAGE_KEY) ?? '[]') as unknown;

    // Issues used to be a chat thread per workspace; those threads are dropped now that Issues is a list.
    return Array.isArray(parsed)
      ? (parsed as (AppChat & { kind?: string })[])
        .filter((chat) => chat.kind !== 'issues')
        .map((chat) => ({
          ...chat,
          messages: chat.messages.map((message) => ({
            ...message,
            resourceIds: Array.isArray(message.resourceIds) ? message.resourceIds : [],
            ...(message.widget ? { widget: normalizeWidget(message.widget) } : {})
          }))
        }))
      : [];
  } catch {
    return [];
  }
};

const writeStoredChats = (chats: AppChat[]): void => {
  try {
    window.localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(chats));
  } catch {
    // Storage can be full or blocked; chats stay in memory for this session.
  }
};

/** Run cards saved before tiers were dropped recorded the launched plan instead of a flag. */
const normalizeWidget = (widget: AppChatWidget): AppChatWidget => {
  if (widget.type === 'pricing' && 'launchedPlan' in widget) {
    const { launchedPlan, ...rest } = widget as typeof widget & { launchedPlan?: unknown };

    return { ...rest, launched: rest.launched ?? Boolean(launchedPlan) };
  }

  return widget;
};

const getUiMetadata = (metadata: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(metadata) || !isRecord(metadata.ui)) {
    return undefined;
  }

  return metadata.ui;
};

export const isWorkflowAutoSelected = (metadata: Record<string, unknown> | undefined): boolean => {
  if (typeof metadata?.workflowAutoSelected === 'boolean') {
    return metadata.workflowAutoSelected;
  }

  // Messages created before workflowAutoSelected was added used the workflow source as the only
  // indication that the backend had picked the workflow.
  return metadata?.workflowSource === 'saved' || metadata?.workflowSource === 'generated';
};

const toAppMessage = (message: ApiChatMessage): AppChatMessage | undefined => {
  if (message.role !== 'user' && message.role !== 'assistant') {
    return undefined;
  }

  const ui = getUiMetadata(message.metadata);
  const issueRefsValue = ui?.issueRefs;
  const widgetValue = ui?.widget;
  const issueRefs = Array.isArray(issueRefsValue) ? issueRefsValue as unknown as AppIssueRef[] : undefined;
  const widget = isRecord(widgetValue) && typeof widgetValue.type === 'string'
    ? normalizeWidget(widgetValue as unknown as AppChatWidget)
    : undefined;
  const metadata = isRecord(message.metadata) ? message.metadata : undefined;
  const isAutoWorkflow = isWorkflowAutoSelected(metadata);

  return {
    id: message.id,
    role: message.role,
    content: message.content,
    resourceIds: Array.isArray(message.resourceIds)
      ? message.resourceIds.filter((id): id is string => typeof id === 'string')
      : [],
    ...(widget ? { widget } : {}),
    ...(issueRefs?.length ? { issueRefs } : {}),
    ...(metadata?.route === 'codex' ? { isCodex: true } : {}),
    ...(isAutoWorkflow ? { isAutoWorkflow } : {}),
    createdAt: message.createdAt
  };
};

const toAppChat = (chat: ApiChat): AppChat => ({
  id: chat.id,
  workspaceId: chat.workspaceId,
  title: chat.title,
  messages: (chat.messages ?? []).map(toAppMessage).filter((message): message is AppChatMessage => Boolean(message)),
  createdAt: chat.createdAt,
  updatedAt: chat.updatedAt
});

const sortChats = (chats: AppChat[]): AppChat[] => {
  return [...chats].sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt));
};

const chatPath = (workspaceId: string, chatId = ''): string => {
  return chatId
    ? getWorkspaceRootPath({ kind: 'chat', id: chatId })
    : `/workspaces/${encodeURIComponent(workspaceId)}/chats`;
};

export function useAppChats(workspaceIds: string[] = [], isSignedIn = false) {
  const isDev = runIsDevGitHubAccountEnabled();
  const [chats, setChats] = useState<AppChat[]>(() => isDev ? readStoredChats() : []);
  const [replyingChatIds, setReplyingChatIds] = useState<Set<string>>(() => new Set());
  /** Chats whose pending reply the backend routed to Codex. */
  const [codexReplyChatIds, setCodexReplyChatIds] = useState<Set<string>>(() => new Set());
  const chatsRef = useRef(chats);
  const cancelledReplyIdsRef = useRef(new Set<string>());
  const workspaceKey = workspaceIds.join('\n');

  chatsRef.current = chats;

  const replaceChats = useCallback((update: (current: AppChat[]) => AppChat[]) => {
    const next = update(chatsRef.current);

    chatsRef.current = next;
    setChats(next);
  }, []);

  useEffect(() => {
    if (isDev) {
      writeStoredChats(chats);
    }
  }, [chats, isDev]);

  useEffect(() => {
    if (isDev) {
      return undefined;
    }

    if (!isSignedIn) {
      replaceChats(() => []);
      return undefined;
    }

    const ids = workspaceKey ? workspaceKey.split('\n') : [];

    if (ids.length === 0) {
      replaceChats(() => []);
      return undefined;
    }

    let cancelled = false;

    Promise.all(ids.map(async (workspaceId) => {
      const response = await runRequestJson<{ chats?: ApiChat[] }>(
        `${chatPath(workspaceId)}?includeMessages=true`
      );

      return (response.chats ?? []).map(toAppChat);
    }))
      .then((groups) => {
        if (!cancelled) {
          replaceChats(() => sortChats(groups.flat()));
        }
      })
      .catch((error: unknown) => {
        console.error('Failed to load chats:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [isDev, isSignedIn, replaceChats, workspaceKey]);

  const updateChat = useCallback((chatId: string, update: (chat: AppChat) => AppChat) => {
    replaceChats((current) => current.map((chat) => chat.id === chatId ? update(chat) : chat));
  }, [replaceChats]);

  /** Load one canonical chat root, used by pasted links without depending on every workspace list. */
  const loadChat = useCallback(async (chatId: string): Promise<AppChat | undefined> => {
    const existing = chatsRef.current.find((chat) => chat.id === chatId);

    if (isDev) {
      return existing;
    }

    const response = await runRequestJson<{ chat: ApiChat }>(
      getWorkspaceRootPath({ kind: 'chat', id: chatId })
    );
    const loaded = toAppChat(response.chat);

    replaceChats((current) => sortChats([loaded, ...current.filter((chat) => chat.id !== loaded.id)]));
    return loaded;
  }, [isDev, replaceChats]);

  /** Appends a user message, creating the chat when it does not exist yet. Returns the chat id. */
  const addUserMessage = useCallback(async (
    target: { chatId: string | null; workspaceId: string },
    content: string,
    options: { issueRefs?: AppIssueRef[]; resourceIds?: string[]; title?: string } = {}
  ): Promise<string> => {
    const now = new Date().toISOString();
    const localMessage: AppChatMessage = {
      ...createMessage('user', content),
      resourceIds: options.resourceIds ?? [],
      ...(options.issueRefs?.length ? { issueRefs: options.issueRefs } : {})
    };
    let existing = target.chatId ? chatsRef.current.find((chat) => chat.id === target.chatId) : undefined;

    if (isDev) {
      if (existing) {
        updateChat(existing.id, (chat) => ({ ...chat, messages: [...chat.messages, localMessage], updatedAt: now }));
        return existing.id;
      }

      const chat: AppChat = {
        id: createId(),
        workspaceId: target.workspaceId,
        title: options.title ?? deriveChatTitle(content),
        messages: [localMessage],
        createdAt: now,
        updatedAt: now
      };

      replaceChats((current) => [chat, ...current]);
      return chat.id;
    }

    if (!existing) {
      const created = await runRequestJson<{ chat: ApiChat }>(chatPath(target.workspaceId), {
        method: 'POST',
        body: JSON.stringify({ title: options.title ?? deriveChatTitle(content) })
      });

      existing = toAppChat(created.chat);
      replaceChats((current) => [existing!, ...current]);
    }

    const response = await runRequestJson<{ userMessage: ApiChatMessage }>(
      `${chatPath(existing.workspaceId, existing.id)}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({
          content,
          respond: false,
          ...(options.resourceIds?.length ? { resourceIds: options.resourceIds } : {}),
          ...(options.issueRefs?.length ? { metadata: { ui: { issueRefs: options.issueRefs } } } : {})
        })
      }
    );
    const message = toAppMessage(response.userMessage);

    if (!message) {
      throw new Error('The saved chat message was invalid.');
    }

    updateChat(existing.id, (chat) => ({
      ...chat,
      messages: [...chat.messages, message],
      updatedAt: message.createdAt
    }));

    return existing.id;
  }, [isDev, replaceChats, updateChat]);

  const addAssistantMessage = useCallback(async (
    chatId: string,
    content: string,
    widget?: AppChatWidget
  ): Promise<AppChatMessage> => {
    const chat = chatsRef.current.find((entry) => entry.id === chatId);

    if (!chat) {
      throw new Error('Chat not found.');
    }

    if (isDev) {
      const message = createMessage('assistant', content, widget);

      updateChat(chatId, (current) => ({
        ...current,
        messages: [...current.messages, message],
        updatedAt: message.createdAt
      }));
      return message;
    }

    const response = await runRequestJson<{ assistantMessage: ApiChatMessage }>(
      `${chatPath(chat.workspaceId, chat.id)}/presentations`,
      {
        method: 'POST',
        body: JSON.stringify({ content, metadata: { ui: { ...(widget ? { widget } : {}) } } })
      }
    );
    const message = toAppMessage(response.assistantMessage);

    if (!message) {
      throw new Error('The saved assistant message was invalid.');
    }

    updateChat(chatId, (current) => ({
      ...current,
      messages: [...current.messages, message],
      updatedAt: message.createdAt
    }));

    return message;
  }, [isDev, updateChat]);

  const updateWidget = useCallback((chatId: string, messageId: string, patch: Partial<AppChatWidget>) => {
    const chat = chatsRef.current.find((entry) => entry.id === chatId);
    const currentMessage = chat?.messages.find((message) => message.id === messageId);

    if (!chat || !currentMessage?.widget) {
      return;
    }

    const widget = { ...currentMessage.widget, ...patch } as AppChatWidget;

    updateChat(chatId, (current) => ({
      ...current,
      messages: current.messages.map((message) => {
        return message.id === messageId ? { ...message, widget } : message;
      })
    }));

    if (!isDev) {
      void runRequestJson(
        `${chatPath(chat.workspaceId, chat.id)}/messages/${encodeURIComponent(messageId)}/presentation`,
        { method: 'PATCH', body: JSON.stringify({ metadata: { ui: { widget } } }) }
      ).catch((error: unknown) => console.error('Failed to update chat card:', error));
    }
  }, [isDev, updateChat]);

  /** Shows the typing indicator in a chat while `task` runs. */
  const withReply = useCallback(async (chatId: string, task: () => Promise<void>) => {
    cancelledReplyIdsRef.current.delete(chatId);
    setReplyingChatIds((current) => new Set(current).add(chatId));

    try {
      await task();
    } catch (error) {
      if (cancelledReplyIdsRef.current.has(chatId) || error instanceof AppChatReportedError) {
        return;
      }

      console.error('Chat action failed:', error);

      try {
        const message = error instanceof Error && /message limit/i.test(error.message)
          ? error.message
          : 'Something went wrong. Please try again.';
        await addAssistantMessage(chatId, message);
      } catch (saveError) {
        console.error('Failed to save chat error message:', saveError);
      }
    } finally {
      cancelledReplyIdsRef.current.delete(chatId);
      setReplyingChatIds((current) => {
        const next = new Set(current);

        next.delete(chatId);
        return next;
      });
    }
  }, [addAssistantMessage]);

  const cancelReply = useCallback(async (chatId: string): Promise<void> => {
    const chat = chatsRef.current.find((entry) => entry.id === chatId);

    cancelledReplyIdsRef.current.add(chatId);
    setReplyingChatIds((current) => {
      const next = new Set(current);

      next.delete(chatId);
      return next;
    });

    if (!isDev && chat) {
      await runRequestJson(`${chatPath(chat.workspaceId, chat.id)}/cancel`, { method: 'POST' });
    }
  }, [isDev]);

  const replyWithText = useCallback((chatId: string) => withReply(chatId, async () => {
    if (isDev) {
      await new Promise((resolve) => window.setTimeout(resolve, ASSISTANT_REPLY_DELAY_MS));
      await addAssistantMessage(chatId, 'Chat responses are not connected in mock mode.');
      return;
    }

    const chat = chatsRef.current.find((entry) => entry.id === chatId);

    if (!chat) {
      throw new Error('Chat not found.');
    }

    // The reply is one request, so its route is polled separately while it runs.
    let isDone = false;
    let pollTimer: number | undefined;
    const pollRoute = () => {
      pollTimer = window.setTimeout(() => {
        void runRequestJson<{ route: string | null }>(`${chatPath(chat.workspaceId, chat.id)}/replies/status`)
          .then((status) => {
            if (isDone) {
              return;
            }

            if (status.route === 'codex') {
              setCodexReplyChatIds((current) => new Set(current).add(chatId));
            } else if (!status.route) {
              pollRoute();
            }
          })
          .catch(() => {
            if (!isDone) {
              pollRoute();
            }
          });
      }, REPLY_ROUTE_POLL_MS);
    };

    pollRoute();

    let response: { assistantMessage: ApiChatMessage };

    try {
      response = await runRequestJson<{ assistantMessage: ApiChatMessage }>(
        `${chatPath(chat.workspaceId, chat.id)}/replies`,
        { method: 'POST' }
      );
    } finally {
      isDone = true;
      window.clearTimeout(pollTimer);
      setCodexReplyChatIds((current) => {
        const next = new Set(current);

        next.delete(chatId);
        return next;
      });
    }

    const message = toAppMessage(response.assistantMessage);

    if (!message) {
      throw new Error('The assistant reply was invalid.');
    }

    updateChat(chatId, (current) => ({
      ...current,
      messages: [...current.messages, message],
      updatedAt: message.createdAt
    }));
  }), [addAssistantMessage, isDev, updateChat, withReply]);

  /**
   * Runs the backend Havu agent's pentest action and persists both sides of the exchange.
   * The backend resolves the active user workflow, or its default workflow, and checks the
   * signed-in account's allowance.
   */
  const runPentest = useCallback(async (
    chatId: string,
    codebaseResourceId: string,
    githubSessionId?: string
  ): Promise<string> => {
    const chat = chatsRef.current.find((entry) => entry.id === chatId);

    if (!chat) {
      throw new Error('Chat not found.');
    }

    const content = 'Run a security assessment.';

    if (isDev) {
      const userMessage = createMessage('user', content);
      const runId = `dev-run-${Date.now()}`;
      const assistantMessage = createMessage('assistant', 'Assessment started.', { type: 'pentest', runId });

      updateChat(chatId, (current) => ({
        ...current,
        messages: [...current.messages, userMessage, assistantMessage],
        updatedAt: assistantMessage.createdAt
      }));
      return runId;
    }

    const response = await runRequestJson<{
      userMessage: ApiChatMessage;
      assistantMessage: ApiChatMessage;
    }>(`${chatPath(chat.workspaceId, chat.id)}/messages`, {
      method: 'POST',
      body: JSON.stringify({
        content,
        action: {
          type: 'pentest',
          codebaseResourceId,
          ...(githubSessionId ? { githubSessionId } : {})
        }
      })
    });
    const userMessage = toAppMessage(response.userMessage);
    const assistantMessage = toAppMessage(response.assistantMessage);

    if (!userMessage || !assistantMessage) {
      throw new Error('The pentest response was invalid.');
    }

    updateChat(chatId, (current) => ({
      ...current,
      messages: [...current.messages, userMessage, assistantMessage],
      updatedAt: assistantMessage.createdAt
    }));

    if (assistantMessage.widget?.type !== 'pentest') {
      throw new AppChatReportedError(assistantMessage.content || 'The assessment could not be started.');
    }

    return assistantMessage.widget.runId;
  }, [isDev, updateChat]);

  const renameChat = useCallback((chatId: string, title: string) => {
    const nextTitle = title.trim();
    const chat = chatsRef.current.find((entry) => entry.id === chatId);

    if (!nextTitle || !chat) {
      return;
    }

    updateChat(chatId, (current) => ({ ...current, title: nextTitle }));

    if (!isDev) {
      void runRequestJson(chatPath(chat.workspaceId, chat.id), {
        method: 'PATCH',
        body: JSON.stringify({ title: nextTitle })
      }).catch((error: unknown) => console.error('Failed to rename chat:', error));
    }
  }, [isDev, updateChat]);

  const deleteChat = useCallback((chatId: string) => {
    const chat = chatsRef.current.find((entry) => entry.id === chatId);

    replaceChats((current) => current.filter((entry) => entry.id !== chatId));

    if (!isDev && chat) {
      void runRequestJson(chatPath(chat.workspaceId, chat.id), { method: 'DELETE' })
        .catch((error: unknown) => console.error('Failed to delete chat:', error));
    }
  }, [isDev, replaceChats]);

  return {
    chats,
    replyingChatIds,
    codexReplyChatIds,
    loadChat,
    addUserMessage,
    addAssistantMessage,
    updateWidget,
    withReply,
    replyWithText,
    cancelReply,
    runPentest,
    renameChat,
    deleteChat
  };
}
